import React, { useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import GridItem from '../../../../../layout/GridComponents/GridItem';
import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { greys, mainColors } from '../../../../../../styling/theme';
import { percentageToNdecialPlaces } from '../../../../../../utilities/numberFormatters';
import { useDispatch } from 'react-redux';
import usePngFromRecharts from '../../../../../../hooks/usePngFromRecharts';
import { addComponentToPdfExport } from '../../../../../../redux/fileExport/actions';
import { PdfComponentType } from '../../../../../../types/redux/pdfExports/FileExportsStore';
import FileSaver from 'file-saver';
import ExportButton from '../../../../../feedback/ExportButton';
import ChartDownloadButton from '../../../../../buttons/ChartDownloadButton';
import { DataObject } from '../../../../../../types/redux/data/dataTypes';

interface FundVsBenchmarkGraphProps {
  data: DataObject;
  benchmark: string;
}

interface FundVsBenchmarkGraphData {
  date: string;
  fundPerformance: number;
  benchmark: number;
}

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0.62rem 1.56rem 0.62rem 0.94rem',
  },
  cardTitle: {
    color: mainColors.mainBlue,
    fontSize: '1.38rem',
    fontWeight: 400,
    margin: '0.62rem 0.94rem',
  },
  customTooltipContainer: {
    backgroundColor: 'white',
    padding: '0.62rem 0.94rem',
    borderRadius: '0.31rem',
    border: `1px solid ${mainColors.mainBlue}`,
    width: '15.62rem',
    color: mainColors.mainBlue,
  },
  tooltipDate: {
    fontSize: '1.25rem',
    fontWeight: 500,
  },
  tooltipFundPerf: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.mainBlue,
  },
  tooltipBenchmark: {
    fontSize: '1.00rem',
    fontWeight: 600,
    color: mainColors.Fail,
  },
  exportButtons: {
    display: 'flex',
    alignItems: 'center',
  },
}));

const CustomTooltip = ({ active, payload, label, benchmark }: any) => {
  const classes = useStyles();
  if (active && payload && payload.length) {
    return (
      <div className={classes.customTooltipContainer}>
        <div>
          <div className={classes.tooltipDate}>{label}</div>
          <hr />
          <div className={classes.tooltipFundPerf}>
            Fund Performance:{' '}
            {percentageToNdecialPlaces(
              payload.find((item: any) => item.name === 'fundPerformance')
                .value / 100,
              2,
            )}
          </div>
          <div className={classes.tooltipBenchmark}>
            {benchmark}:{' '}
            {percentageToNdecialPlaces(
              payload.find((item: any) => item.name === 'benchmark').value /
                100,
              2,
            )}
          </div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
};

const buildGraphData = (inputData: any): FundVsBenchmarkGraphData[] => {
  if (!inputData.data || !inputData.data.length) {
    return [];
  } else {
    const returnData: any[] = [];
    inputData.data[0].fund_vs_benchmark.map((date: any) => {
      returnData.push({
        date: date.Date,
        fundPerformance: date.running_pl_Fund,
        benchmark: date.running_pl_Benchmark,
      });
    });
    return returnData.sort(
      (a: FundVsBenchmarkGraphData, b: FundVsBenchmarkGraphData) =>
        a.date.localeCompare(b.date, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
    );
  }
};

const FundVsBenchmarkGraph: React.FC<FundVsBenchmarkGraphProps> = ({
  data,
  benchmark,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const builtGraphData = buildGraphData(data);

  const id = `fund-vs-${benchmark}-graph`;
  const title = `Fund Performance vs ${benchmark}`;

  const { ref, handleDownload } = usePngFromRecharts();

  useEffect(() => {
    dispatch(
      addComponentToPdfExport({
        identifier: id,
        handler: handleDownload,
        type: PdfComponentType.LINE_CHART,
        title: title,
      }),
    );
  }, [ref]);

  const saveImage = async () => {
    const imageData = await handleDownload();
    if (imageData) {
      FileSaver.saveAs(imageData.data, `${id}.png`);
    }
  };

  return (
    <GridItem xs={12} card>
      <div className={classes.toolbar}>
        <h2 className={classes.cardTitle}>{title}</h2>
        <div className={classes.exportButtons}>
          <ExportButton
            fileName={`${id}.csv`}
            exportData={builtGraphData}
            fields={Object.keys(builtGraphData[0])}
            fieldsMap={[
              { key: 'date', label: 'Date' },
              { key: 'fundPerformance', label: 'Fund Performance' },
              { key: 'benchmark', label: benchmark },
            ]}
          />
          <ChartDownloadButton handler={saveImage} />
        </div>
      </div>
      <ResponsiveContainer width="100%" height={530}>
        <LineChart
          data={builtGraphData}
          margin={{ top: 20, right: 50, bottom: 20, left: 30 }}
          ref={ref}
        >
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={60}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
          />
          <YAxis
            width={70}
            tickFormatter={(tickItem) => {
              return percentageToNdecialPlaces(tickItem / 100, 1);
            }}
            tick={{
              stroke: mainColors.mainBlue,
              strokeWidth: 1,
              fontSize: '0.81rem',
            }}
            tickLine={true}
          >
            <Label
              style={{
                textAnchor: 'middle',
                fontSize: '125%',
                fontWeight: 400,
                color: mainColors.mainBlue,
              }}
              angle={270}
              value={'Cumulative Movement %'}
              position={'insideLeft'}
            />
          </YAxis>
          <Tooltip content={<CustomTooltip benchmark={benchmark} />} />
          <CartesianGrid stroke={greys.grey300} strokeDasharray="5 5" />
          <ReferenceLine y={0} stroke={greys.grey600} />
          <Line
            type="linear"
            dataKey="fundPerformance"
            stroke={mainColors.mainBlue}
            strokeWidth={2}
          />
          <Line
            type="linear"
            dataKey="benchmark"
            stroke={mainColors.Fail}
            strokeWidth={2}
          />
          <Legend
            verticalAlign="top"
            height={30}
            wrapperStyle={{ fontSize: '1.00rem' }}
            formatter={(item) => {
              switch (item) {
                case 'fundPerformance':
                  return 'Fund Performance';
                case 'benchmark':
                  return benchmark;
                default:
                  return item;
              }
            }}
          />
        </LineChart>
      </ResponsiveContainer>
    </GridItem>
  );
};

export default FundVsBenchmarkGraph;
